import React, { useState, useEffect } from 'react';
import { CONFIG } from '../config';
import { CopyIcon, RefreshIcon } from '../components/icons/Icons';

const SelfRegistrationSettingsPage = () => {
  const [selectedEvent, setSelectedEvent] = useState('');
  const [availableEvents, setAvailableEvents] = useState([]);
  const [registrationData, setRegistrationData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  // Fetch available events with names from admin API
  useEffect(() => {
    const fetchUserEvents = async () => {
      try {
        // Fetch user info for event_access and events list for names
        const [userResp, eventsResp] = await Promise.all([
          fetch(`${CONFIG.apiBaseUrl}/user-info`, { credentials: 'include' }),
          fetch(`${CONFIG.apiBaseUrl}/admin/events`, { credentials: 'include' }),
        ]);

        if (userResp.ok) {
          const userData = await userResp.json();
          const eventAccess = userData.event_access || '';
          const accessList = eventAccess.split(',').map(e => e.trim()).filter(e => e.length > 0);

          // Build event objects with names from admin API
          let eventsMap = {};
          if (eventsResp.ok) {
            const eventsData = await eventsResp.json();
            (eventsData.events || []).forEach(ev => {
              eventsMap[ev.event_id] = ev.event_name || ev.event_id;
            });
          }

          const events = accessList.map(id => ({
            id,
            name: eventsMap[id] || id,
          }));

          setAvailableEvents(events);

          // Auto-select first event or previously saved event
          const saved = localStorage.getItem('selfRegistrationSelectedEvent');
          if (saved && events.some(e => e.id === saved)) {
            setSelectedEvent(saved);
          } else if (events.length === 1) {
            setSelectedEvent(events[0].id);
          }
        }
      } catch (err) {
        console.error('Failed to fetch user events:', err);
        setError('Failed to load your event access');
      } finally {
        setLoadingEvents(false);
      }
    };

    fetchUserEvents();
  }, []);

  const generateToken = async () => {
    if (!selectedEvent) {
      setError('Please select an event');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const resp = await fetch(`${CONFIG.apiBaseUrl}/admin/generate-registration-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ eventId: selectedEvent }),
      });

      if (!resp.ok) {
        const data = await resp.json();
        throw new Error(data.error || 'Failed to generate token');
      }

      const data = await resp.json();
      setRegistrationData(data);

      // Save selected event for convenience
      localStorage.setItem('selfRegistrationSelectedEvent', selectedEvent);
    } catch (err) {
      console.error('Token generation failed:', err);
      setError(err.message || 'Failed to generate registration link');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const getQRCodeUrl = (url) => {
    // Using free QR code API (no dependencies required)
    return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(url)}`;
  };

  return (
    <div className="admin-page">
      <h2 className="page-title">Self-Registration Settings</h2>
      <p className="page-subtitle">
        Generate a unique registration link and QR code for guests to self-register for your event.
      </p>

      <div className="settings-section">
        <h3>Generate Registration Link</h3>

        {loadingEvents ? (
          <div className="loading-message">Loading your events...</div>
        ) : availableEvents.length === 0 ? (
          <div className="error-message">
            No events found. Please contact an administrator to grant you event access.
          </div>
        ) : (
          <>
            <div className="form-group">
              <label htmlFor="eventSelect" className="form-label">
                Select Event
              </label>
              <select
                id="eventSelect"
                value={selectedEvent}
                onChange={(e) => setSelectedEvent(e.target.value)}
                className="form-input"
                disabled={loading}
              >
                <option value="">-- Select an event --</option>
                {availableEvents.map((event) => (
                  <option key={event.id} value={event.id}>
                    {event.name}
                  </option>
                ))}
              </select>
              <div className="input-hint">
                Select the event to generate a self-registration link for guests.
              </div>
            </div>
            <div className="form-group">
              <button
                onClick={generateToken}
                className="btn btn-primary"
                disabled={loading || !selectedEvent}
              >
                {loading ? 'Generating...' : <><RefreshIcon /> Generate</>}
              </button>
            </div>
          </>
        )}

        {error && (
          <div className="error-message">{error}</div>
        )}

        {registrationData && (
          <div className="registration-result">
            <h3>Registration Link Generated</h3>

            <div className="result-grid">
              <div className="result-section">
                <h4>Registration URL</h4>
                <div className="url-display">
                  <code className="url-text">{registrationData.registrationUrl}</code>
                  <button
                    onClick={() => copyToClipboard(registrationData.registrationUrl)}
                    className="btn btn-secondary btn-copy"
                    title="Copy to clipboard"
                  >
                    <CopyIcon />
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                  <button
                    onClick={() => window.open(registrationData.registrationUrl, '_blank')}
                    className="btn btn-secondary btn-copy"
                    title="Open in new window"
                  >
                    Open
                  </button>
                </div>

                <div className="token-info">
                  <p><strong>Event:</strong> {availableEvents.find(e => e.id === registrationData.eventId)?.name || registrationData.eventId}</p>
                </div>
              </div>

              <div className="result-section qr-section">
                <h4>QR Code</h4>
                <div className="qr-code-container">
                  <img
                    src={getQRCodeUrl(registrationData.registrationUrl)}
                    alt="Registration QR Code"
                    className="qr-code-image"
                  />
                </div>
                <p className="qr-hint">
                  Guests can scan this QR code to open the registration page.
                </p>
              </div>
            </div>

            <div className="usage-instructions">
              <h4>How to Use</h4>
              <ol>
                <li>Share the registration URL or QR code with your guests</li>
                <li>Guests will be able to register themselves for event notifications</li>
                <li>Registrations will appear in your guest list for {availableEvents.find(e => e.id === registrationData.eventId)?.name || registrationData.eventId}</li>
              </ol>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SelfRegistrationSettingsPage;
